import React from 'react'
import {
  MapPin,
  DollarSign,
  TrendingUp,
  Calendar,
  Home,
  MoreVertical,
  Edit,
  Eye,
  Trash2,
  BarChart3,
  Clock
} from 'lucide-react'
import { PropertyResponse } from '@/services/api'
import { Button } from './Button'
import { getRenovationCosts, calculateMonthlyPayment, safeNumber } from '@/utils/propertyCalculations'

interface PropertyCardProps {
  property: PropertyResponse
  onEdit?: (property: PropertyResponse) => void
  onDelete?: (property: PropertyResponse) => void
  onView?: (property: PropertyResponse) => void
  showActions?: boolean
  compactView?: boolean
  isSelected?: boolean
  onToggleSelect?: (propertyId: string) => void
}

export function PropertyCard({ 
  property, 
  onEdit, 
  onDelete, 
  onView,
  showActions = true,
  compactView = false,
  isSelected = false,
  onToggleSelect
}: PropertyCardProps) {
  const [showDropdown, setShowDropdown] = React.useState(false)

  // Calculate key metrics
  const totalInvestment = safeNumber(property.purchase_price) + getRenovationCosts(property.renovation_costs)

  const monthlyMortgage = calculateMonthlyPayment(
    safeNumber(property.loan_amount),
    safeNumber(property.interest_rate),
    safeNumber(property.loan_term, 30)
  )
  
  const monthlyExpenses = monthlyMortgage +
    (safeNumber(property.annual_taxes) / 12) +
    (safeNumber(property.annual_insurance) / 12) +
    safeNumber(property.monthly_hoa_fees)

  const monthlyCashFlow = safeNumber(property.expected_monthly_rent) - monthlyExpenses
  const downPayment = safeNumber(property.down_payment)
  const cashOnCashReturn = downPayment > 0
    ? ((monthlyCashFlow * 12) / downPayment) * 100
    : 0

  const getAnalysisTypeColor = (status: string) => {
    switch (status) {
      case 'buy-hold':
        return 'bg-blue-100 text-blue-800'
      case 'flip':
        return 'bg-green-100 text-green-800'
      case 'brrr':
        return 'bg-purple-100 text-purple-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getAnalysisTypeLabel = (status: string) => {
    switch (status) {
      case 'buy-hold':
        return 'Buy & Hold'
      case 'flip':
        return 'Fix & Flip'
      case 'brrr':
        return 'BRRR'
      default:
        return 'Analysis'
    }
  }

  const formatLastAnalysisDate = (date?: string) => {
    if (!date) return null

    const now = new Date()
    const analysisDate = new Date(date)
    const diffInDays = Math.floor((now.getTime() - analysisDate.getTime()) / (1000 * 60 * 60 * 24))

    if (diffInDays === 0) return 'Today'
    if (diffInDays === 1) return 'Yesterday'
    if (diffInDays < 7) return `${diffInDays} days ago`
    if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`
    if (diffInDays < 365) return `${Math.floor(diffInDays / 30)} months ago`
    return analysisDate.toLocaleDateString()
  }

  const getAnalysisTypesFromMetadata = () => {
    if (!property.analysis_metadata) return []

    const types = []
    if (property.analysis_metadata['buy-hold']) types.push({ type: 'buy-hold', count: property.analysis_metadata['buy-hold'].count })
    if (property.analysis_metadata['flip']) types.push({ type: 'flip', count: property.analysis_metadata['flip'].count })
    if (property.analysis_metadata['brrr']) types.push({ type: 'brrr', count: property.analysis_metadata['brrr'].count })

    return types
  }

  if (compactView) {
    return (
      <div className={`bg-white rounded-lg border p-4 hover:shadow-md transition-shadow ${
        isSelected ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200'
      }`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {onToggleSelect && (
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => onToggleSelect(property.property_id)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
            )}
            <div className="p-2 bg-blue-100 rounded-lg">
              <Home className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-medium text-gray-900">{property.property_address}</h3>
              <p className="text-sm text-gray-500">
                {property.city}, {property.state} {property.zip_code}
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-lg font-semibold text-gray-900">
              ${property.purchase_price.toLocaleString()}
            </p>
            <p className={`text-sm ${monthlyCashFlow >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              ${monthlyCashFlow.toLocaleString()}/mo
            </p>
            {(property.analysis_count || 0) > 0 && (
              <div className="flex items-center justify-end mt-1 text-xs text-gray-500">
                <BarChart3 className="w-3 h-3 mr-1" />
                {property.analysis_count} analyses
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={`relative bg-white rounded-lg border overflow-hidden hover:shadow-lg transition-shadow ${
      isSelected ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200'
    }`}>
      {/* Header */}
      <div className="p-6 pb-4">
        {onToggleSelect && (
          <div className="absolute top-4 left-4">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => onToggleSelect(property.property_id)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
          </div>
        )}
        <div className={`flex items-start justify-between ${onToggleSelect ? 'ml-8' : ''}`}>
          <div className="flex-1">
            <div className="flex items-center flex-wrap gap-2 mb-2">
              {/* Show analysis types from metadata if available, otherwise fallback to status */}
              {property.analysis_metadata && getAnalysisTypesFromMetadata().length > 0 ? (
                getAnalysisTypesFromMetadata().map(({ type, count }) => (
                  <span key={type} className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getAnalysisTypeColor(type)}`}>
                    {getAnalysisTypeLabel(type)}
                    {count > 1 && <span className="ml-1 font-bold">×{count}</span>}
                  </span>
                ))
              ) : (
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getAnalysisTypeColor(property.analysis_status)}`}>
                  {getAnalysisTypeLabel(property.analysis_status)}
                </span>
              )}

              {/* Total analysis count badge */}
              {(property.analysis_count || 0) > 1 && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                  <BarChart3 className="w-3 h-3 mr-1" />
                  {property.analysis_count} total
                </span>
              )}

              {property.property_type && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                  {property.property_type.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </span>
              )}
            </div>
            
            <h3 className="text-lg font-semibold text-gray-900 mb-1">
              {property.property_address}
            </h3>
            
            <div className="flex items-center text-gray-500 text-sm">
              <MapPin className="w-4 h-4 mr-1" />
              {property.city}, {property.state} {property.zip_code}
            </div>
          </div>

          {showActions && (
            <div className="relative">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowDropdown(!showDropdown)}
                className="p-2"
              >
                <MoreVertical className="w-4 h-4" />
              </Button>
              
              {showDropdown && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 border">
                  <div className="py-1">
                    {onView && (
                      <button
                        onClick={() => {
                          onView(property)
                          setShowDropdown(false)
                        }}
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        View Analysis
                      </button>
                    )}
                    {onEdit && (
                      <button
                        onClick={() => {
                          onEdit(property)
                          setShowDropdown(false)
                        }}
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Edit Property
                      </button>
                    )}
                    {onDelete && (
                      <button
                        onClick={() => {
                          onDelete(property)
                          setShowDropdown(false)
                        }}
                        className="flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50 w-full text-left"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete Property
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="px-6 pb-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                  Purchase Price
                </p>
                <p className="text-lg font-semibold text-gray-900">
                  ${property.purchase_price.toLocaleString()}
                </p>
              </div>
              <div className="p-2 bg-blue-100 rounded">
                <DollarSign className="w-4 h-4 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                  Monthly Cash Flow
                </p>
                <p className={`text-lg font-semibold ${monthlyCashFlow >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  ${monthlyCashFlow.toLocaleString()}
                </p>
              </div>
              <div className="p-2 bg-green-100 rounded">
                <TrendingUp className="w-4 h-4 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                  Cash-on-Cash ROI
                </p>
                <p className={`text-lg font-semibold ${cashOnCashReturn >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {cashOnCashReturn.toFixed(1)}%
                </p>
              </div>
              <div className="p-2 bg-purple-100 rounded">
                <TrendingUp className="w-4 h-4 text-purple-600" />
              </div>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                  Total Investment
                </p>
                <p className="text-lg font-semibold text-gray-900">
                  ${totalInvestment.toLocaleString()}
                </p>
              </div>
              <div className="p-2 bg-orange-100 rounded">
                <DollarSign className="w-4 h-4 text-orange-600" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex flex-col space-y-1">
            <div className="flex items-center text-sm text-gray-500">
              <Calendar className="w-4 h-4 mr-1" />
              Created {new Date(property.created_at).toLocaleDateString()}
            </div>
            {property.last_analysis_date && (
              <div className="flex items-center text-sm text-gray-500">
                <Clock className="w-4 h-4 mr-1" />
                Last analysis {formatLastAnalysisDate(property.last_analysis_date)}
              </div>
            )}
          </div>

          {onView && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => onView(property)}
            >
              View Details
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

// Helper function to calculate monthly mortgage payment
